import { supabase } from '../lib/supabase';
import { MotoristaProfile, MotoristaStats, ComprovanteEntregaData, OcorrenciaItem, VistoriaVeiculo, ExtratoRepasseItem } from '../types/areaMotorista';
import { VeiculoChecklist } from '../types/veiculoExtra';
import { OrdemDeServico } from '../types/ordemServico';
import { pessoaBelongsToGroup } from '../utils/pessoaGroupUtils';
import { sanitizeDeep } from '../utils/textSanitizer';

export const areaMotoristaService = {
  /**
   * Retorna lista de motoristas e agregados cadastrados no sistema
   */
  async getMotoristas(): Promise<MotoristaProfile[]> {
    try {
      // Busca todas as pessoas para identificar motoristas e agregados
      const { data: pessoas, error: errPessoas } = await supabase
        .from('pessoa')
        .select('*')
        .order('nome', { ascending: true });

      if (errPessoas || !pessoas) {
        console.error('Erro ao buscar pessoas para motoristas:', errPessoas);
        return [];
      }

      // Busca veículos para cruzar dados de motoristas vinculados
      const { data: veiculos } = await supabase
        .from('veiculos')
        .select('*');

      // Conjunto de IDs de pessoas vinculadas como motorista ou agregado em algum veículo
      const pessoaIdsEmVeiculos = new Set<number>();
      (veiculos || []).forEach(v => {
        if (v.motorista_id) pessoaIdsEmVeiculos.add(Number(v.motorista_id));
        if (v.agregado_id) pessoaIdsEmVeiculos.add(Number(v.agregado_id));
      });

      // Filtra apenas pessoas que são realmente motoristas, agregados ou vinculadas a veículos
      const pessoasFiltradas = pessoas.filter(p => {
        // Se estiver vinculado a algum veículo, é motorista/agregado
        if (pessoaIdsEmVeiculos.has(p.id)) return true;

        // Se tiver CNH preenchida, é motorista
        if (p.numero_cnh || p.obs?.toLowerCase().includes('cnh:')) return true;

        // Usa filtro de grupo de pessoas do sistema (exclui empresas/clientes/fornecedores como Abdala Engenharia)
        return pessoaBelongsToGroup(p, 'motoristas');
      });

      const motoristas: MotoristaProfile[] = pessoasFiltradas.map(p => {
        // Encontra veículo vinculado caso exista
        const veic = (veiculos || []).find(v => v.motorista_id === p.id || v.agregado_id === p.id);

        const isAgregadoByVeic = veic?.agregado_id === p.id;
        const isAgregadoByCat = Number(p.categoria_cliente_id) === 4;
        const isAgregadoByObs = p.obs?.toLowerCase().includes('agregado');

        const tipoVinculo = (isAgregadoByVeic || isAgregadoByCat || isAgregadoByObs) ? 'Agregado' : 'Motorista';

        return {
          id: p.id,
          nome: p.nome,
          nome_fantasia: p.nome_fantasia,
          documento: p.documento || 'Não informado',
          email: p.email,
          fone: p.fone,
          cnh: p.numero_cnh || (p.obs?.includes('CNH:') ? p.obs.split('CNH:')[1]?.split(';')[0]?.trim() : undefined),
          veiculo_id: veic?.id,
          placa: veic?.placa,
          tipo_vinculo: tipoVinculo,
          status_disponibilidade: 'disponivel'
        };
      });

      return sanitizeDeep(motoristas);
    } catch (err) {
      console.error('Erro inesperado em getMotoristas:', err);
      return [];
    }
  },

  /**
   * Retorna perfil completo do motorista por ID
   */
  async getMotoristaById(id: number): Promise<MotoristaProfile | null> {
    try {
      const { data: p, error } = await supabase
        .from('pessoa')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error || !p) return null;

      const { data: veic } = await supabase
        .from('veiculos')
        .select('*')
        .or(`motorista_id.eq.${id},agregado_id.eq.${id}`)
        .maybeSingle();

      return sanitizeDeep({
        id: p.id,
        nome: p.nome,
        nome_fantasia: p.nome_fantasia,
        documento: p.documento,
        email: p.email,
        fone: p.fone,
        veiculo_id: veic?.id,
        placa: veic?.placa,
        tipo_vinculo: p.tipo_cliente_id === 2 ? 'Agregado' : 'Motorista',
        status_disponibilidade: 'disponivel'
      });
    } catch (err) {
      console.error('Erro ao buscar motorista por ID:', err);
      return null;
    }
  },

  /**
   * Retorna todas as Ordens de Serviço atribuídas ao motorista/agregado
   */
  async getOrdensServico(motoristaId: number): Promise<OrdemDeServico[]> {
    try {
      // 1. Busca por agregado = motoristaId
      const { data: osAgregado, error: err1 } = await supabase
        .from('ordem_de_servico')
        .select('*')
        .eq('agregado', motoristaId)
        .order('data_atendimento', { ascending: false });

      // 2. Busca veículos vinculados ao motorista
      const { data: veiculos } = await supabase
        .from('veiculos')
        .select('id, placa')
        .or(`motorista_id.eq.${motoristaId},agregado_id.eq.${motoristaId}`);

      const veiculoIds = (veiculos || []).map(v => v.id).filter(Boolean);
      const placas = (veiculos || []).map(v => v.placa).filter(Boolean);

      let osPorVeiculo: OrdemDeServico[] = [];
      if (veiculoIds.length > 0) {
        const { data: osV } = await supabase
          .from('ordem_de_servico')
          .select('*')
          .in('veiculos_id', veiculoIds)
          .order('data_atendimento', { ascending: false });
        if (osV) osPorVeiculo = osV;
      }

      // Combina e remove duplicatas
      const mapaOS = new Map<number, OrdemDeServico>();
      (osAgregado || []).forEach(os => mapaOS.set(os.id, os));
      osPorVeiculo.forEach(os => mapaOS.set(os.id, os));

      const lista = Array.from(mapaOS.values()).sort((a, b) => {
        const dataA = a.data_atendimento ? new Date(a.data_atendimento).getTime() : 0;
        const dataB = b.data_atendimento ? new Date(b.data_atendimento).getTime() : 0;
        return dataB - dataA;
      });

      return sanitizeDeep(lista);
    } catch (err) {
      console.error('Erro ao buscar ordens de serviço do motorista:', err);
      return [];
    }
  },

  /**
   * Calcula estatísticas operacionais e financeiras do motorista
   */
  async getStats(motoristaId: number): Promise<MotoristaStats> {
    try {
      const listaOS = await this.getOrdensServico(motoristaId);
      const hojeStr = new Date().toISOString().split('T')[0];

      let viagensHoje = 0;
      let viagensEmAndamento = 0;
      let viagensConcluidas = 0;
      let totalGanhosMes = 0;
      let totalRepassesPendentes = 0;
      let totalRepassesPagos = 0;

      listaOS.forEach(os => {
        const dataAtendimento = os.data_atendimento ? os.data_atendimento.split('T')[0] : '';
        const repasse = Number(os.repasse) || 0;

        if (dataAtendimento === hojeStr) {
          viagensHoje++;
        }

        // Status: 1 = Agendado, 2 = Em Andamento, 3 = Cancelado, 4 = Finalizado, etc.
        if (os.status_id === 2 || os.status_id === 1) {
          viagensEmAndamento++;
        }

        if (os.status_id === 4 || os.assinatura) {
          viagensConcluidas++;
          totalRepassesPagos += repasse;
        } else {
          totalRepassesPendentes += repasse;
        }

        totalGanhosMes += repasse;
      });

      return {
        viagensHoje,
        viagensEmAndamento,
        viagensConcluidas,
        totalViagensMes: listaOS.length,
        totalGanhosMes,
        totalRepassesPendentes,
        totalRepassesPagos,
        kmRodadoAproximado: viagensConcluidas * 45 // Estimativa média
      };
    } catch (err) {
      console.error('Erro ao calcular estatísticas do motorista:', err);
      return {
        viagensHoje: 0,
        viagensEmAndamento: 0,
        viagensConcluidas: 0,
        totalViagensMes: 0,
        totalGanhosMes: 0,
        totalRepassesPendentes: 0,
        totalRepassesPagos: 0,
        kmRodadoAproximado: 0
      };
    }
  },

  /**
   * Atualiza status da viagem (ex: Iniciar, Chegada na Coleta, Em Trânsito, Chegada no Destino)
   */
  async atualizarStatusOS(
    osId: number,
    novoStatusId: number,
    etapaNome: string,
    observacao?: string
  ): Promise<boolean> {
    try {
      const agora = new Date();
      const horaAtual = agora.toTimeString().split(' ')[0].substring(0, 5);

      const updatePayload: Partial<OrdemDeServico> = {
        status_id: novoStatusId,
        updated_at: agora.toISOString()
      };

      // Se iniciou agora, grava horario_inicio se vazio
      if (novoStatusId === 2) {
        updatePayload.horario_inicio = horaAtual;
      }

      // Adiciona log de etapa nas ocorrências/histórico
      if (etapaNome) {
        const { data: currentOS } = await supabase
          .from('ordem_de_servico')
          .select('ocorrencias')
          .eq('id', osId)
          .single();

        const logEtapa = `[${agora.toLocaleDateString('pt-BR')} ${horaAtual}] Status: ${etapaNome}${observacao ? ` - ${observacao}` : ''}`;
        const historicoAntigo = currentOS?.ocorrencias ? `${currentOS.ocorrencias}\n` : '';
        updatePayload.ocorrencias = `${historicoAntigo}${logEtapa}`;
      }

      const { error } = await supabase
        .from('ordem_de_servico')
        .update(updatePayload)
        .eq('id', osId);

      if (error) {
        console.error('Erro ao atualizar status da OS:', error);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Erro inesperado ao atualizar status da OS:', err);
      return false;
    }
  },

  /**
   * Salva o comprovante digital de finalização de entrega com assinatura e fotos
   */
  async salvarComprovanteEntrega(comprovante: ComprovanteEntregaData): Promise<boolean> {
    try {
      const agora = new Date();
      const horaAtual = agora.toTimeString().split(' ')[0].substring(0, 5);

      const { data: currentOS } = await supabase
        .from('ordem_de_servico')
        .select('ocorrencias, horario_inicio')
        .eq('id', comprovante.os_id)
        .single();

      const logFinalizacao = `[${agora.toLocaleDateString('pt-BR')} ${horaAtual}] Entrega Concluída com Sucesso. Recebido por: ${comprovante.nome_recebedor} (Doc: ${comprovante.documento_recebedor}${comprovante.grau_parentesco_cargo ? ` - ${comprovante.grau_parentesco_cargo}` : ''})${comprovante.observacoes ? ` - Obs: ${comprovante.observacoes}` : ''}`;
      const historicoAntigo = currentOS?.ocorrencias ? `${currentOS.ocorrencias}\n` : '';

      const { error } = await supabase
        .from('ordem_de_servico')
        .update({
          status_id: 4, // Finalizado
          horario_final: horaAtual,
          assinatura: comprovante.assinatura_base64,
          rg_assinatura: comprovante.documento_recebedor,
          ocorrencias: `${historicoAntigo}${logFinalizacao}`,
          updated_at: agora.toISOString()
        })
        .eq('id', comprovante.os_id);

      if (error) {
        console.error('Erro ao salvar comprovante de entrega na OS:', error);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Erro ao finalizar OS com comprovante:', err);
      return false;
    }
  },

  /**
   * Registra ocorrência durante o trajeto (ex: trânsito, atraso, cliente ausente)
   */
  async registrarOcorrencia(ocorrencia: OcorrenciaItem): Promise<boolean> {
    try {
      const agora = new Date();
      const horaAtual = agora.toTimeString().split(' ')[0].substring(0, 5);

      const { data: currentOS } = await supabase
        .from('ordem_de_servico')
        .select('ocorrencias')
        .eq('id', ocorrencia.os_id)
        .single();

      const tipoFormatado = ocorrencia.tipo.toUpperCase().replace('_', ' ');
      const logOcorrencia = `[OCORRÊNCIA ${agora.toLocaleDateString('pt-BR')} ${horaAtual}] Tipo: ${tipoFormatado} - ${ocorrencia.descricao}`;
      const historicoAntigo = currentOS?.ocorrencias ? `${currentOS.ocorrencias}\n` : '';

      const { error } = await supabase
        .from('ordem_de_servico')
        .update({
          ocorrencias: `${historicoAntigo}${logOcorrencia}`,
          updated_at: agora.toISOString()
        })
        .eq('id', ocorrencia.os_id);

      if (error) {
        console.error('Erro ao registrar ocorrência na OS:', error);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Erro ao registrar ocorrência:', err);
      return false;
    }
  },

  /**
   * Salva vistoria / checklist de inspeção veicular diária na tabela
   * `veiculo_checklists` do Supabase (mesma tabela usada pela tela de
   * checklist do lado da empresa). Antes isso ficava só no `localStorage` do
   * celular do motorista: a gestão nunca via as vistorias, e trocar de
   * aparelho apagava todo o histórico.
   */
  async salvarVistoria(vistoria: VistoriaVeiculo): Promise<boolean> {
    try {
      const colunaPorItem: Record<string, keyof VeiculoChecklist> = {
        '1': 'pneus_ok',
        '2': 'freios_ok',
        '3': 'oleo_ok',
        '4': 'agua_ok',
        '5': 'luzes_ok',
        '6': 'documentacao_ok',
        '7': 'equipamentos_seguranca_ok',
        '8': 'lataria_limpeza_ok',
      };

      const flags: Record<string, boolean> = {
        pneus_ok: true, freios_ok: true, oleo_ok: true, agua_ok: true,
        luzes_ok: true, documentacao_ok: true, equipamentos_seguranca_ok: true, lataria_limpeza_ok: true,
      };
      let temCritico = false;
      let temAtencao = false;
      vistoria.itens.forEach((item) => {
        const coluna = colunaPorItem[item.id];
        if (coluna) flags[coluna as string] = item.status === 'ok';
        if (item.status === 'critico') temCritico = true;
        else if (item.status === 'atencao') temAtencao = true;
      });

      const statusGeral: VeiculoChecklist['status_geral'] = temCritico
        ? 'reprovado'
        : temAtencao
        ? 'com_observacoes'
        : 'aprovado';

      const payload = {
        veiculo_id: vistoria.veiculo_id || null,
        motorista_id: vistoria.motorista_id,
        data_checklist: vistoria.data_vistoria,
        km_inicial: vistoria.km_inicial,
        status_geral: statusGeral,
        combustivel_nivel: vistoria.nivel_combustivel,
        observacoes: vistoria.observacoes_gerais || null,
        itens_detalhados: vistoria.itens,
        ...flags,
      };

      const { error } = await supabase.from('veiculo_checklists').insert([payload]);
      if (error) {
        console.error('Erro ao salvar vistoria no Supabase:', error.message);
        return false;
      }
      return true;
    } catch (err) {
      console.error('Erro inesperado ao salvar vistoria:', err);
      return false;
    }
  },

  /**
   * Retorna histórico de vistorias do motorista, lendo direto do Supabase.
   */
  async getVistorias(motoristaId: number): Promise<VistoriaVeiculo[]> {
    try {
      const { data, error } = await supabase
        .from('veiculo_checklists')
        .select('*')
        .eq('motorista_id', motoristaId)
        .order('data_checklist', { ascending: false })
        .limit(30);

      if (error) {
        console.error('Erro ao buscar vistorias do Supabase:', error.message);
        return [];
      }

      const nomePorId: Record<string, string> = {
        pneus_ok: 'Pneus e Estepe (calibragem e desgaste)',
        freios_ok: 'Freios (resposta e nível de fluido)',
        oleo_ok: 'Nível de Óleo do Motor',
        agua_ok: 'Água do Radiador / Arrefecimento',
        luzes_ok: 'Faróis, Lanternas e Setas de Direção',
        documentacao_ok: 'Documentação do Veículo (CRLV em dia)',
        equipamentos_seguranca_ok: 'Equipamentos Obrigatórios (Triângulo, Macaco, Chave de Roda)',
        lataria_limpeza_ok: 'Limpeza e Higienização do Baú / Carroceria',
      };
      const colunaParaId: Record<string, string> = {
        pneus_ok: '1', freios_ok: '2', oleo_ok: '3', agua_ok: '4',
        luzes_ok: '5', documentacao_ok: '6', equipamentos_seguranca_ok: '7', lataria_limpeza_ok: '8',
      };

      return (data || []).map((row: any): VistoriaVeiculo => {
        // Se o detalhe completo (com "atenção") foi salvo, usa ele. Senão,
        // reconstrói a partir das colunas booleanas (perde a distinção entre
        // "atenção" e "crítico" para registros antigos, ambos viram "crítico").
        const itens = Array.isArray(row.itens_detalhados) && row.itens_detalhados.length > 0
          ? row.itens_detalhados
          : Object.keys(colunaParaId).map((coluna) => ({
              id: colunaParaId[coluna],
              item: nomePorId[coluna],
              categoria: 'geral',
              status: row[coluna] === false ? 'critico' : 'ok',
            }));

        return {
          id: row.id,
          motorista_id: row.motorista_id,
          veiculo_id: row.veiculo_id || undefined,
          placa: undefined,
          data_vistoria: row.data_checklist,
          km_inicial: row.km_inicial || 0,
          nivel_combustivel: (row.combustivel_nivel || '3/4') as VistoriaVeiculo['nivel_combustivel'],
          itens,
          aprovado: row.status_geral !== 'reprovado',
          observacoes_gerais: row.observacoes || undefined,
          created_at: row.created_at,
        };
      });
    } catch (err) {
      console.error('Erro inesperado ao buscar vistorias:', err);
      return [];
    }
  },

  /**
   * Retorna extrato consolidado de repasses por viagem
   */
  async getExtratoRepasses(motoristaId: number): Promise<ExtratoRepasseItem[]> {
    try {
      const listaOS = await this.getOrdensServico(motoristaId);

      return listaOS.map(os => {
        const repasse = Number(os.repasse) || 0;
        const ajudanteRepasse = (Number(os.quantidade_ajudante) || 0) * (Number(os.valor_unit_ajud) || 0);
        const despesas = Number(os.valor_despesa) || 0;
        const valorLiquido = repasse - despesas;
        const isFinalizado = os.status_id === 4 || !!os.assinatura;

        return {
          os_id: os.id,
          data_atendimento: os.data_atendimento || new Date().toISOString(),
          descricao: os.descricao || `Transporte OS #${os.id}`,
          retirada: os.retirada || 'Não especificado',
          entrega: os.entrega || 'Não especificado',
          valor_repasse: repasse,
          valor_ajudantes: ajudanteRepasse,
          valor_despesas: despesas,
          valor_liquido_motorista: valorLiquido,
          status_pagamento: isFinalizado ? 'pago' : 'pendente',
          status_os: isFinalizado ? 'Finalizado' : 'Em Execução'
        };
      });
    } catch (err) {
      console.error('Erro ao obter extrato de repasses:', err);
      return [];
    }
  }
};
